import {
    AlertTriangle,
    ArrowRight,
    Check,
    CheckCircle2,
    CircleX,
    LoaderCircle,
    RefreshCw,
    ShieldCheck,
    Users,
    WandSparkles,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FaArrowRightLong } from 'react-icons/fa6'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './PayrollStaffReadinessScreen.module.css'

const STATUS_ORDER = {
    BLOCKED: 0,
    WARNING: 1,
    READY: 2,
}

const FILTERS = {
    ALL: 'ALL',
    BLOCKED: 'BLOCKED',
    WARNING: 'WARNING',
    READY: 'READY',
}

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const normalizeStaffList = (payload) => {
    const candidates = [
        payload?.staff,
        payload?.staffReadiness,
        payload?.staffList,
        payload?.response,
    ]

    const staffArray = candidates.find(Array.isArray) || []

    return staffArray.map((item, index) => ({
        staffIdentifier: item?.staffIdentifier || item?.employeeIdentifier || item?.id || `staff-${index}`,
        staffName: item?.staffName || item?.name || item?.fullName || 'Unnamed Staff',
        staffCode: item?.staffCode || item?.employeeCode || '',
        readinessStatus: String(item?.readinessStatus || 'BLOCKED').toUpperCase(),
        checks: {
            salaryStructure: Boolean(item?.checks?.salaryStructure),
            incentivePlan: Boolean(item?.checks?.incentivePlan),
            leaveBalance: Boolean(item?.checks?.leaveBalance),
            shiftConfigured: Boolean(item?.checks?.shiftConfigured),
        },
        issues: Array.isArray(item?.issues) ? item.issues.filter(Boolean) : [],
    }))
}

const getActionFromIssues = (issues = [], staffIdentifier = '') => {
    const firstIssue = String(issues?.[0] || '').toLowerCase()

    if (!firstIssue) return null

    if (firstIssue.includes('salary structure')) {
        return {
            label: 'Assign Salary',
            path: '/payroll/assign-salary-structure',
            setupScreenName: 'AssignSalaryStructureScreen',
        }
    }

    if (firstIssue.includes('leave balance')) {
        return {
            label: 'Initialize Leave',
            path: '/payroll/setup/leave-balance-init',
            setupScreenName: 'LeaveBalanceInitScreen',
        }
    }

    if (firstIssue.includes('shift')) {
        return {
            label: 'Configure Shift',
            path: staffIdentifier ? `/staff/${staffIdentifier}/attendance` : '/payroll/setup/shift-config',
            setupScreenName: 'ShiftConfigScreen',
        }
    }

    if (firstIssue.includes('commission') || firstIssue.includes('incentive')) {
        return {
            label: 'Assign Commission',
            path: '/payroll/assign-incentive-plan',
            setupScreenName: 'AssignIncentivePlanScreen',
        }
    }

    return null
}

const defaultPayload = {
    totalActiveStaff: 0,
    blockedCount: 0,
    warningCount: 0,
    readyCount: 0,
    staff: [],
}

const PayrollStaffReadinessScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)
    const storeIdentifier = activeStore?.storeIdentifier || ''

    const initialFilter = location?.state?.preferredFilter
    const [activeFilter, setActiveFilter] = useState(
        Object.values(FILTERS).includes(initialFilter) ? initialFilter : FILTERS.ALL
    )
    const [isLoading, setIsLoading] = useState(true)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [apiMessage, setApiMessage] = useState('')
    const [data, setData] = useState(defaultPayload)

    const fetchStaffReadiness = useCallback(async ({ silent = false } = {}) => {
        if (!storeIdentifier) {
            setErrorMessage('Please select a store first to view staff readiness.')
            setData(defaultPayload)
            setIsLoading(false)
            return
        }

        if (!silent) {
            setIsLoading(true)
        } else {
            setIsRefreshing(true)
        }
        setErrorMessage('')

        try {
            const response = await fetch(BASE_URL_API + 'getPayrollStaffReadiness', {
                method: 'POST',
                cache: 'no-store',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier,
                }),
            })

            if (!response.ok) {
                throw new Error('Server Error')
            }

            const result = await response.json()

            if (result?.status === 'success') {
                const payload = result?.response || {}
                const staff = normalizeStaffList(payload)
                const computed = staff.reduce((acc, item) => {
                    if (item.readinessStatus === FILTERS.BLOCKED) acc.blockedCount += 1
                    else if (item.readinessStatus === FILTERS.WARNING) acc.warningCount += 1
                    else if (item.readinessStatus === FILTERS.READY) acc.readyCount += 1
                    return acc
                }, { blockedCount: 0, warningCount: 0, readyCount: 0 })

                setApiMessage(result?.message || '')
                setData({
                    totalActiveStaff: Number(payload?.totalActiveStaff ?? staff.length),
                    blockedCount: Number(payload?.blockedCount ?? computed.blockedCount),
                    warningCount: Number(payload?.warningCount ?? computed.warningCount),
                    readyCount: Number(payload?.readyCount ?? computed.readyCount),
                    staff,
                })
                return
            }

            if (result?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            setErrorMessage(result?.message || 'Unable to fetch staff readiness details.')
        } catch {
            setErrorMessage('Unable to connect to server. Please try again.')
        } finally {
            setIsLoading(false)
            setIsRefreshing(false)
        }
    }, [authCtx, storeIdentifier])

    useEffect(() => {
        fetchStaffReadiness({ silent: false })
    }, [fetchStaffReadiness])

    useEffect(() => {
        if (!storeIdentifier) return
        fetchStaffReadiness({ silent: true })
    }, [location.key, fetchStaffReadiness, storeIdentifier])

    useEffect(() => {
        const onFocus = () => {
            if (!storeIdentifier) return
            fetchStaffReadiness({ silent: true })
        }

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') onFocus()
        }

        window.addEventListener('focus', onFocus)
        document.addEventListener('visibilitychange', onVisibilityChange)

        return () => {
            window.removeEventListener('focus', onFocus)
            document.removeEventListener('visibilitychange', onVisibilityChange)
        }
    }, [fetchStaffReadiness, storeIdentifier])

    const counts = useMemo(() => ({
        ALL: Number(data?.totalActiveStaff || 0),
        BLOCKED: Number(data?.blockedCount || 0),
        WARNING: Number(data?.warningCount || 0),
        READY: Number(data?.readyCount || 0),
    }), [data?.blockedCount, data?.readyCount, data?.totalActiveStaff, data?.warningCount])

    const filteredStaff = useMemo(() => {
        const list = Array.isArray(data?.staff) ? [...data.staff] : []

        list.sort((a, b) => {
            const orderA = STATUS_ORDER[a?.readinessStatus] ?? 99
            const orderB = STATUS_ORDER[b?.readinessStatus] ?? 99
            if (orderA !== orderB) return orderA - orderB
            return String(a?.staffName || '').localeCompare(String(b?.staffName || ''))
        })

        if (activeFilter === FILTERS.ALL) return list
        return list.filter((item) => item?.readinessStatus === activeFilter)
    }, [activeFilter, data?.staff])

    const statusPills = [
        { key: FILTERS.READY, label: 'Ready', icon: CheckCircle2, count: counts.READY, className: styles.pillReady },
        { key: FILTERS.WARNING, label: 'Warning', icon: AlertTriangle, count: counts.WARNING, className: styles.pillWarning },
        { key: FILTERS.BLOCKED, label: 'Blocked', icon: CircleX, count: counts.BLOCKED, className: styles.pillBlocked },
    ]

    const filterTabs = [
        { key: FILTERS.ALL, label: `All (${counts.ALL})` },
        { key: FILTERS.BLOCKED, label: `Blocked (${counts.BLOCKED})` },
        { key: FILTERS.WARNING, label: `Warning (${counts.WARNING})` },
        { key: FILTERS.READY, label: `Ready (${counts.READY})` },
    ]

    const totalActiveStaff = Number(data?.totalActiveStaff || 0)

    const onTakeAction = (staff) => {
        const action = getActionFromIssues(staff?.issues, staff?.staffIdentifier)
        if (!action) return

        navigate(action.path, {
            state: {
                source: '/payroll/staff-readiness',
                setupScreenName: action.setupScreenName,
                staffIdentifier: staff?.staffIdentifier,
                prefillStaffIdentifier: staff?.staffIdentifier,
            },
        })
    }

    const renderCheckItem = (isPassed, label) => (
        <div className={styles.checkItem}>
            <span className={isPassed ? styles.checkPass : styles.checkFail}>
                {isPassed ? <Check size={13} /> : <CircleX size={13} />}
            </span>
            <span>{label}</span>
        </div>
    )

    return (
        <div className={styles.page}>
            <section className={styles.hero}>
                <div className={styles.heroTop}>
                    <span className={styles.heroPill}><WandSparkles size={14} /> Payroll Staff Readiness</span>
                    {isRefreshing && (
                        <span className={styles.refreshHint}><RefreshCw size={13} className={styles.spinning} /> Syncing latest updates...</span>
                    )}
                </div>

                <div className={styles.heroMain}>
                    <div>
                        <h1>See exactly who is payroll-ready — and who needs action.</h1>
                        <p>
                            This is the drill-down from setup summary: same counts, now mapped person-by-person with one-click fix actions.
                        </p>
                    </div>
                    <Link to="/payroll/setup" className={styles.backToSetupBtn}>
                        Back to Payroll Setup <FaArrowRightLong size={13} />
                    </Link>
                </div>

                <div className={styles.summaryPills}>
                    {statusPills.map((pill) => {
                        const Icon = pill.icon
                        const isActive = activeFilter === pill.key
                        return (
                            <button
                                key={pill.key}
                                type="button"
                                className={`${styles.summaryPill} ${pill.className} ${isActive ? styles.summaryPillActive : ''}`}
                                onClick={() => setActiveFilter(pill.key)}
                            >
                                <Icon size={14} /> {pill.count} {pill.label}
                            </button>
                        )
                    })}
                </div>
            </section>

            {!storeIdentifier && (
                <section className={styles.alertCard}>
                    <AlertTriangle size={16} />
                    <div>
                        <strong>Select an active store first.</strong>
                        <p>Staff readiness is store-specific. Please select a store to continue.</p>
                    </div>
                </section>
            )}

            {errorMessage && (
                <section className={`${styles.alertCard} ${styles.errorCard}`}>
                    <AlertTriangle size={16} />
                    <div>
                        <strong>Could not load staff readiness.</strong>
                        <p>{errorMessage}</p>
                        <button type="button" onClick={() => fetchStaffReadiness({ silent: false })} className={styles.retryBtn}>
                            <RefreshCw size={14} /> Retry
                        </button>
                    </div>
                </section>
            )}

            {!errorMessage && !isLoading && totalActiveStaff === 0 && (
                <section className={styles.emptyStateCard}>
                    <Users size={20} />
                    <div>
                        <h3>No active staff found.</h3>
                        <p>Add staff to your roster before checking payroll readiness.</p>
                    </div>
                    <Link to="/manage/employees" className={styles.goStaffBtn}>
                        Go to Staff <ArrowRight size={14} />
                    </Link>
                </section>
            )}

            {!errorMessage && !isLoading && totalActiveStaff > 0 && (
                <>
                    <section className={styles.filterTabsRow}>
                        {filterTabs.map((tab) => {
                            const isActive = activeFilter === tab.key
                            return (
                                <button
                                    key={tab.key}
                                    type="button"
                                    className={`${styles.filterTab} ${isActive ? styles.filterTabActive : ''}`}
                                    onClick={() => setActiveFilter(tab.key)}
                                >
                                    {tab.label}
                                </button>
                            )
                        })}
                    </section>

                    {apiMessage && <p className={styles.apiMessage}>{apiMessage}</p>}

                    <section className={styles.cardsGrid}>
                        {filteredStaff.map((staff) => {
                            const status = staff?.readinessStatus
                            const statusClass = status === 'READY'
                                ? styles.cardReady
                                : status === 'WARNING'
                                    ? styles.cardWarning
                                    : styles.cardBlocked
                            const action = getActionFromIssues(staff?.issues, staff?.staffIdentifier)
                            const showAction = status !== 'READY' && action

                            return (
                                <article
                                    key={staff?.staffIdentifier}
                                    className={`${styles.staffCard} ${statusClass}`}
                                    onClick={() => navigate(`/payroll/staff-compensation/${staff?.staffIdentifier}`, {
                                        state: { staffIdentifier: staff?.staffIdentifier, staffName: staff?.staffName },
                                    })}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                            navigate(`/payroll/staff-compensation/${staff?.staffIdentifier}`, {
                                                state: { staffIdentifier: staff?.staffIdentifier, staffName: staff?.staffName },
                                            })
                                        }
                                    }}
                                >
                                    <div className={styles.cardTopRow}>
                                        <div>
                                            <h3>{staff?.staffName}</h3>
                                            {staff?.staffCode && <p>{staff.staffCode}</p>}
                                        </div>
                                        <span className={`${styles.statusBadge} ${styles[`badge${status}`]}`}>{status}</span>
                                    </div>

                                    <div className={styles.checksGrid}>
                                        {renderCheckItem(staff?.checks?.salaryStructure, 'Salary Structure')}
                                        {renderCheckItem(staff?.checks?.incentivePlan, 'Incentive Plan')}
                                        {renderCheckItem(staff?.checks?.leaveBalance, 'Leave Balance')}
                                        {renderCheckItem(staff?.checks?.shiftConfigured, 'Shift Configured')}
                                    </div>

                                    {Array.isArray(staff?.issues) && staff.issues.length > 0 && (
                                        <div className={styles.issuesWrap}>
                                            {staff.issues.map((issue, index) => (
                                                <p key={`${staff?.staffIdentifier}-issue-${index}`}>
                                                    <AlertTriangle size={13} /> {issue}
                                                </p>
                                            ))}
                                        </div>
                                    )}

                                    {showAction && (
                                        <button
                                            type="button"
                                            className={styles.actionBtn}
                                            onClick={(event) => {
                                                event.stopPropagation()
                                                onTakeAction(staff)
                                            }}
                                        >
                                            {action.label} <ArrowRight size={14} />
                                        </button>
                                    )}
                                </article>
                            )
                        })}
                    </section>
                </>
            )}

            {isLoading && (
                <section className={styles.loaderBox}>
                    <LoaderCircle size={20} className={styles.spinning} />
                    <p>Loading payroll staff readiness...</p>
                </section>
            )}

            <section className={styles.footerHint}>
                <ShieldCheck size={14} />
                <span>Tip: Resolve BLOCKED first, then WARNING, to unlock cleaner payroll runs.</span>
            </section>
        </div>
    )
}

export default PayrollStaffReadinessScreen